import maplibregl from 'maplibre-gl';
import store from '../state';
import { getApiServer } from '../config';

interface SearchResult {
  name: string;
  state?: string;
  country?: string;
  kind?: string;
  lat: number;
  lon: number;
  isCoordinate?: boolean;
  isExample?: boolean;
  subtitle?: string;
}

// Array item format in search shards: [name, state, country, kind, lat, lon]
type SearchIndexItem = [string, string, string, string, number, number];

interface IndexedSearchItem {
  raw: SearchIndexItem;
  name: string;
  nameLower: string;
  nameTokens: string[];
  nameJoined: string;
  stateNorm: string;
  stateFullName: string;
  countryNorm: string;
  kindNorm: string;
  allMetaTokens: string[];
}

// Geographic Abbreviations & Canonical Expansions
const GEO_ABBREVIATIONS: Record<string, string> = {
  mt: 'mount',
  'mt.': 'mount',
  mte: 'mount',
  mtn: 'mountain',
  mtns: 'mountains',
  st: 'saint',
  'st.': 'saint',
  ste: 'sainte',
  'ste.': 'sainte',
  pk: 'peak',
  'pk.': 'peak',
  pks: 'peaks',
  pt: 'point',
  'pt.': 'point',
  ft: 'fort',
  'ft.': 'fort',
  lk: 'lake',
  'lk.': 'lake',
  lks: 'lakes',
  np: 'national park',
  'n.p.': 'national park',
  sp: 'state park',
  's.p.': 'state park',
  nf: 'national forest',
  'n.f.': 'national forest',
  twp: 'township',
  'twp.': 'township',
  is: 'island',
  isl: 'island',
  isls: 'islands',
  rdg: 'ridge',
  vly: 'valley',
  crk: 'creek',
  riv: 'river',
  cyn: 'canyon',
  hts: 'heights',
  hl: 'hill',
  hls: 'hills',
  bt: 'butte',
  jct: 'junction',
  res: 'reservoir',
  bch: 'beach',
  spr: 'spring',
  sprs: 'springs',
  hbr: 'harbor'
};

// US States & Canadian Provinces mapping (2-letter postal code to full name)
const STATE_MAP: Record<string, string> = {
  al: 'alabama', ak: 'alaska', az: 'arizona', ar: 'arkansas', ca: 'california',
  co: 'colorado', ct: 'connecticut', de: 'delaware', fl: 'florida', ga: 'georgia',
  hi: 'hawaii', id: 'idaho', il: 'illinois', in: 'indiana', ia: 'iowa',
  ks: 'kansas', ky: 'kentucky', la: 'louisiana', me: 'maine', md: 'maryland',
  ma: 'massachusetts', mi: 'michigan', mn: 'minnesota', ms: 'mississippi',
  mo: 'missouri', mt: 'montana', ne: 'nebraska', nv: 'nevada', nh: 'new hampshire',
  nj: 'new jersey', nm: 'new mexico', ny: 'new york', nc: 'north carolina',
  nd: 'north dakota', oh: 'ohio', ok: 'oklahoma', or: 'oregon', pa: 'pennsylvania',
  ri: 'rhode island', sc: 'south carolina', sd: 'south dakota', tn: 'tennessee',
  tx: 'texas', ut: 'utah', vt: 'vermont', va: 'virginia', wa: 'washington',
  wv: 'west virginia', wi: 'wisconsin', wy: 'wyoming', dc: 'district of columbia',
  ab: 'alberta', bc: 'british columbia', mb: 'manitoba', nb: 'new brunswick',
  nl: 'newfoundland', ns: 'nova scotia', nt: 'northwest territories',
  nu: 'nunavut', on: 'ontario', pe: 'prince edward island', qc: 'quebec',
  sk: 'saskatchewan', yt: 'yukon'
};

const SEARCH_EXAMPLES: SearchResult[] = [
  {
    name: '46.8523, -121.7603',
    subtitle: '📍 Lat / Long GPS Coordinates (Mount Rainier Summit)',
    lat: 46.8523,
    lon: -121.7603,
    isExample: true,
    isCoordinate: true,
    kind: 'gps'
  },
  {
    name: 'Mount Rainier',
    state: 'WA',
    country: 'USA',
    kind: 'volcano',
    subtitle: '🌋 Mountain Peak / Volcano • WA, USA',
    lat: 46.8523,
    lon: -121.7603,
    isExample: true
  },
  {
    name: 'Packwood, WA',
    state: 'WA',
    country: 'USA',
    kind: 'city',
    subtitle: '🏙️ City / Town • WA, USA',
    lat: 46.6500,
    lon: -121.6553,
    isExample: true
  }
];

export class SearchBarWidget {
  private container!: HTMLElement;
  private inputElement!: HTMLInputElement;
  private autocompleteList!: HTMLElement;
  private debounceTimer: any = null;

  private loadedShards = new Map<string, IndexedSearchItem[]>();
  private pendingShards = new Map<string, Promise<IndexedSearchItem[]>>();

  constructor(
    private map: maplibregl.Map,
    private parentElement: HTMLElement,
    private onSelectLocation?: (lat: number, lon: number, name: string) => void
  ) {
    this.render();
  }

  private render() {
    this.container = document.createElement('div');
    this.container.className = 'gw-search-widget';

    const searchIcon = document.createElement('span');
    searchIcon.className = 'gw-search-icon';
    searchIcon.innerHTML = '🔍';

    this.inputElement = document.createElement('input');
    this.inputElement.type = 'text';
    this.inputElement.className = 'gw-search-input';
    this.inputElement.placeholder = 'Search peak, city, or coords (e.g. 46.85, -121.76)...';
    this.inputElement.autocomplete = 'off';
    this.inputElement.spellcheck = false;

    // Autocomplete dropdown container
    this.autocompleteList = document.createElement('div');
    this.autocompleteList.className = 'gw-search-autocomplete gw-hidden';

    this.inputElement.addEventListener('focus', () => {
      const query = this.inputElement.value.trim();
      if (query.length < 2) {
        this.renderExamples();
        if (query.length === 1) {
          this.preloadShard(query);
        }
      } else {
        this.executeSearch(query);
      }
    });

    this.inputElement.addEventListener('click', (e) => {
      e.stopPropagation();
      const query = this.inputElement.value.trim();
      if (query.length < 2) {
        this.renderExamples();
        if (query.length === 1) {
          this.preloadShard(query);
        }
      }
    });

    this.inputElement.addEventListener('input', () => {
      clearTimeout(this.debounceTimer);
      const query = this.inputElement.value.trim();
      if (query.length === 0) {
        this.renderExamples();
        return;
      }
      if (query.length === 1) {
        this.preloadShard(query);
        this.renderExamples();
        return;
      }
      this.debounceTimer = setTimeout(() => {
        this.executeSearch(query);
      }, 50);
    });

    this.inputElement.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        const firstResult = this.autocompleteList.querySelector('.gw-autocomplete-item') as HTMLElement;
        if (firstResult && firstResult.dataset.lat && firstResult.dataset.lon) {
          const lat = parseFloat(firstResult.dataset.lat);
          const lon = parseFloat(firstResult.dataset.lon);
          const name = firstResult.dataset.name || this.inputElement.value.trim();
          this.handleSelect(lat, lon, name);
          this.closeAutocomplete();
        } else {
          // Check if input itself is direct coordinates
          const parsed = this.parseCoordinates(this.inputElement.value.trim());
          if (parsed) {
            this.handleSelect(parsed.lat, parsed.lon, `${parsed.lat.toFixed(4)}, ${parsed.lon.toFixed(4)}`);
            this.closeAutocomplete();
          }
        }
      } else if (e.key === 'Escape') {
        this.closeAutocomplete();
      }
    });

    document.addEventListener('click', (e) => {
      if (!this.container.contains(e.target as Node)) {
        this.closeAutocomplete();
      }
    });

    this.container.appendChild(searchIcon);
    this.container.appendChild(this.inputElement);
    this.container.appendChild(this.autocompleteList);
    this.parentElement.appendChild(this.container);
  }

  private getShardKey(char: string): string {
    const c = (char || '').toLowerCase();
    if (c >= 'a' && c <= 'z') return c;
    return '0-9';
  }

  private preloadShard(query: string) {
    if (!query) return;
    const key = this.getShardKey(query[0]);
    this.loadShard(key).catch(() => {});
  }

  private preprocessShard(rawItems: SearchIndexItem[]): IndexedSearchItem[] {
    const processed: IndexedSearchItem[] = new Array(rawItems.length);
    for (let i = 0; i < rawItems.length; i++) {
      const item = rawItems[i];
      const name = item[0] || '';
      const state = item[1] || '';
      const country = item[2] || '';
      const kind = item[3] || '';

      const nameTokens = this.normalizeTokens(name);
      const nameJoined = nameTokens.join(' ');
      const nameLower = name.toLowerCase();

      const stateNorm = state.toLowerCase();
      const stateFullName = STATE_MAP[stateNorm] || '';
      const countryNorm = country.toLowerCase();
      const kindNorm = kind.toLowerCase();

      const allMetaTokens = [
        ...nameTokens,
        stateNorm,
        stateFullName,
        countryNorm,
        kindNorm
      ].filter(Boolean);

      processed[i] = {
        raw: item,
        name,
        nameLower,
        nameTokens,
        nameJoined,
        stateNorm,
        stateFullName,
        countryNorm,
        kindNorm,
        allMetaTokens
      };
    }
    return processed;
  }

  private async loadShard(letter: string): Promise<IndexedSearchItem[]> {
    const key = this.getShardKey(letter);
    if (this.loadedShards.has(key)) {
      return this.loadedShards.get(key)!;
    }
    if (this.pendingShards.has(key)) {
      return this.pendingShards.get(key)!;
    }

    const loadPromise = (async () => {
      const cacheKey = `gw-search-shard-${key}-v2`;
      const cacheName = 'gw-search-cache-shards-v2';

      // 1. Check local persistent CacheStorage
      try {
        if ('caches' in window) {
          // Clear legacy monolithic cache if present
          caches.delete('gw-search-cache-v2').catch(() => {});
          caches.delete('gw-search-cache').catch(() => {});

          const cache = await caches.open(cacheName);
          const cachedResp = await cache.match(cacheKey);
          if (cachedResp) {
            const rawData: SearchIndexItem[] = await cachedResp.json();
            const indexed = this.preprocessShard(rawData);
            this.loadedShards.set(key, indexed);
            return indexed;
          }
        }
      } catch (e) {
        console.warn(`CacheStorage read warning for shard ${key}:`, e);
      }

      // 2. Fetch from network
      // Try local relative path first (hosted on Cloudflare Pages / public), then apiServer tileRoot fallback
      const manifest = store.getState().manifest;
      const apiServer = getApiServer(manifest);
      const tileRoot = (manifest ? manifest.tile_base : "tiles").replace(/^\/+|\/+$/g, '');

      const urlsToTry = [
        `search_shards/${key}.json`,
        `${apiServer.replace(/\/$/, '')}/${tileRoot}/search_shards/${key}.json`.replace(/([^:])\/\/+/g, '$1/')
      ];

      let rawData: SearchIndexItem[] | null = null;
      for (const url of urlsToTry) {
        try {
          const res = await fetch(url);
          const contentType = res.headers.get('content-type') || '';
          if (res.ok && (contentType.includes('application/json') || res.status === 200)) {
            rawData = await res.json();
            if (Array.isArray(rawData)) {
              // Cache response asynchronously in background
              if ('caches' in window) {
                caches.open(cacheName).then(cache => {
                  cache.put(cacheKey, new Response(JSON.stringify(rawData), {
                    headers: { 'Content-Type': 'application/json' }
                  })).catch(() => {});
                }).catch(() => {});
              }
              break;
            }
          }
        } catch {
          // try next url
        }
      }

      if (!rawData || !Array.isArray(rawData)) {
        console.warn(`Could not load search shard ${key}`);
        return [];
      }

      const indexed = this.preprocessShard(rawData);
      this.loadedShards.set(key, indexed);
      return indexed;
    })();

    this.pendingShards.set(key, loadPromise);
    try {
      return await loadPromise;
    } finally {
      this.pendingShards.delete(key);
    }
  }

  /**
   * Parses various lat/long coordinate formats:
   *  - "46.8523, -121.7603" or "46.8523,-121.7603"
   *  - "46.8523 -121.7603"
   *  - "46.8523° N, 121.7603° W" or "46.8523N 121.7603W"
   *  - "46.8523 S, 121.7603 E"
   */
  public parseCoordinates(query: string): { lat: number; lon: number } | null {
    const raw = query.trim();
    if (!raw || raw.length < 3) return null;

    // 1. Check for cardinal direction format (e.g., 46.8523N, 121.7603W or 46.8523 N 121.7603 W)
    const cardinalRegex = /^(-?\d+(?:\.\d+)?)\s*°?\s*([NSEWnsew])?[,\s]+(-?\d+(?:\.\d+)?)\s*°?\s*([NSEWnsew])?$/i;
    const cardMatch = raw.match(cardinalRegex);
    if (cardMatch) {
      let val1 = parseFloat(cardMatch[1]);
      const dir1 = (cardMatch[2] || '').toUpperCase();
      let val2 = parseFloat(cardMatch[3]);
      const dir2 = (cardMatch[4] || '').toUpperCase();

      if (!isNaN(val1) && !isNaN(val2)) {
        if (dir1 === 'S') val1 = -Math.abs(val1);
        if (dir1 === 'W') val1 = -Math.abs(val1);
        if (dir2 === 'S') val2 = -Math.abs(val2);
        if (dir2 === 'W') val2 = -Math.abs(val2);

        let lat = val1;
        let lon = val2;

        // If first is marked E/W or second is N/S, swap
        if (dir1 === 'E' || dir1 === 'W' || dir2 === 'N' || dir2 === 'S') {
          lat = val2;
          lon = val1;
        }

        if (lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180) {
          return { lat, lon };
        }
      }
    }

    // 2. Standard decimal format: 46.8523, -121.7603 or 46.8523 -121.7603
    const decimalRegex = /^(-?\d{1,3}(?:\.\d+)?)[,\s]+(-?\d{1,3}(?:\.\d+)?)$/;
    const decMatch = raw.match(decimalRegex);
    if (decMatch) {
      let lat = parseFloat(decMatch[1]);
      let lon = parseFloat(decMatch[2]);

      // If user typed lon first (e.g., -121.76, 46.85), auto-detect based on valid lat bounds
      if ((lat > 90 || lat < -90) && lon >= -90 && lon <= 90 && lat >= -180 && lat <= 180) {
        const temp = lat;
        lat = lon;
        lon = temp;
      }

      if (lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180) {
        return { lat, lon };
      }
    }

    return null;
  }

  /**
   * Tokenizes and normalizes text by expanding abbreviations and stripping punctuation.
   */
  private normalizeTokens(text: string): string[] {
    return text
      .toLowerCase()
      .replace(/[,\/#!$%\^&\*;:{}=\_`~()]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .split(' ')
      .filter(t => t.length > 0)
      .map(t => {
        // Strip trailing period if present
        const clean = t.replace(/\.$/, '');
        if (GEO_ABBREVIATIONS[t]) return GEO_ABBREVIATIONS[t];
        if (GEO_ABBREVIATIONS[clean]) return GEO_ABBREVIATIONS[clean];
        return clean;
      });
  }

  /**
   * Calculates Levenshtein edit distance between two strings with early termination.
   */
  private levenshteinDistance(s1: string, s2: string): number {
    if (s1 === s2) return 0;
    if (s1.length === 0) return s2.length;
    if (s2.length === 0) return s1.length;

    const row: number[] = [];
    for (let i = 0; i <= s2.length; i++) {
      row[i] = i;
    }

    for (let i = 1; i <= s1.length; i++) {
      let prev = i;
      for (let j = 1; j <= s2.length; j++) {
        let val: number;
        if (s1[i - 1] === s2[j - 1]) {
          val = row[j - 1];
        } else {
          val = Math.min(row[j - 1] + 1, prev + 1, row[j] + 1);
        }
        row[j - 1] = prev;
        prev = val;
      }
      row[s2.length] = prev;
    }

    return row[s2.length];
  }

  private async executeSearch(query: string) {
    const rawTrimmed = query.trim();
    if (rawTrimmed.length < 2) {
      this.renderExamples();
      return;
    }

    const parsedCoords = this.parseCoordinates(rawTrimmed);
    const qTokens = this.normalizeTokens(rawTrimmed);
    const qJoined = qTokens.join(' ');
    const rawLower = rawTrimmed.toLowerCase();

    const matches: { item: SearchResult; score: number }[] = [];

    // 1. If query is a valid coordinate, add instant GPS navigation item at highest score
    if (parsedCoords) {
      matches.push({
        item: {
          name: `${parsedCoords.lat.toFixed(4)}, ${parsedCoords.lon.toFixed(4)}`,
          subtitle: `📍 GPS Coordinates (Jump to location)`,
          lat: parsedCoords.lat,
          lon: parsedCoords.lon,
          isCoordinate: true,
          kind: 'gps'
        },
        score: 200
      });
    }

    // Determine candidate shards to search
    const candidateKeys = new Set<string>();
    candidateKeys.add(this.getShardKey(rawTrimmed[0]));
    if (qTokens.length > 0 && qTokens[0].length > 0) {
      candidateKeys.add(this.getShardKey(qTokens[0][0]));
    }
    // If multi-word query (e.g. "Mount Rainier" or "Toutle River"), also check second word's shard
    if (qTokens.length > 1 && qTokens[1].length > 0) {
      candidateKeys.add(this.getShardKey(qTokens[1][0]));
    }

    // Load candidate shards concurrently
    const shardArrays = await Promise.all(
      Array.from(candidateKeys).map(k => this.loadShard(k))
    );

    // Merge and deduplicate candidates across loaded shards
    const seenRaw = new Set<SearchIndexItem>();
    const candidates: IndexedSearchItem[] = [];
    for (let sIdx = 0; sIdx < shardArrays.length; sIdx++) {
      const arr = shardArrays[sIdx];
      for (let i = 0; i < arr.length; i++) {
        const c = arr[i];
        if (!seenRaw.has(c.raw)) {
          seenRaw.add(c.raw);
          candidates.push(c);
        }
      }
    }

    if (candidates.length > 0 && qTokens.length > 0) {
      for (let i = 0; i < candidates.length; i++) {
        const c = candidates[i];
        const { raw, name, nameLower, nameTokens, nameJoined, allMetaTokens, kindNorm } = c;
        const [, state, country, kind, lat, lon] = raw;

        let score = 0;

        // Exact full name match
        if (nameLower === rawLower) {
          score = 100;
        }
        // Exact normalized name match (handles "Mt Hood" vs "Mount Hood")
        else if (nameJoined === qJoined) {
          score = 95;
        }
        // Normalized name starts with query
        else if (nameJoined.startsWith(qJoined)) {
          score = 85;
        }
        // Check if all query tokens are present in candidate tokens
        else {
          let matchedTokens = 0;
          let matchedInName = 0;

          for (let qIdx = 0; qIdx < qTokens.length; qIdx++) {
            const qToken = qTokens[qIdx];
            // Check in name tokens
            let inName = false;
            for (let ntIdx = 0; ntIdx < nameTokens.length; ntIdx++) {
              const nt = nameTokens[ntIdx];
              if (nt === qToken || nt.startsWith(qToken)) {
                inName = true;
                break;
              }
            }

            if (inName) {
              matchedTokens++;
              matchedInName++;
            } else {
              // Check in metadata tokens (state, country, kind)
              let inMeta = false;
              for (let mtIdx = 0; mtIdx < allMetaTokens.length; mtIdx++) {
                const mt = allMetaTokens[mtIdx];
                if (mt === qToken || mt.startsWith(qToken)) {
                  inMeta = true;
                  break;
                }
              }
              if (inMeta) {
                matchedTokens++;
              }
            }
          }

          if (matchedTokens === qTokens.length) {
            // All tokens matched
            if (matchedInName === qTokens.length) {
              score = 75; // All tokens in name
            } else {
              score = 65; // Multi-field match (e.g. "Packwood, WA")
            }
          } else if (qTokens.length >= 3 && matchedInName > 0 && matchedTokens >= qTokens.length - 1) {
            score = 45; // Partial multi-token match
          } else if (nameLower.includes(rawLower)) {
            score = 50; // Substring match
          }
        }

        // Fuzzy / Typo matching if no high score yet and query token is at least 3 chars
        if (score === 0 && qTokens.length === 1 && qTokens[0].length >= 3) {
          const qToken = qTokens[0];
          for (let ntIdx = 0; ntIdx < nameTokens.length; ntIdx++) {
            const nt = nameTokens[ntIdx];
            if (nt.length >= 3) {
              const maxDist = qToken.length >= 6 ? 2 : 1;
              const dist = this.levenshteinDistance(qToken, nt);
              if (dist <= maxDist) {
                score = 30 - dist * 5;
                break;
              }
            }
          }
        }

        if (score > 0) {
          // Bonus for outdoor recreational features & summits
          if (['volcano', 'peak', 'national_park', 'state_park', 'national_monument', 'wilderness', 'trail', 'trailhead', 'river', 'lake', 'park'].includes(kindNorm)) {
            score += 4;
          }
          matches.push({
            item: { name, state, country, kind, lat, lon },
            score
          });
        }
      }
    }

    // Sort by relevance score descending
    matches.sort((a, b) => b.score - a.score);

    // Limit to top 6 results
    const topResults: SearchResult[] = matches.slice(0, 6).map(m => m.item);

    if (topResults.length > 0) {
      this.renderAutocompleteResults(topResults);
    } else {
      this.closeAutocomplete();
    }
  }

  private renderExamples() {
    this.renderAutocompleteResults(SEARCH_EXAMPLES, true);
  }

  private renderAutocompleteResults(results: SearchResult[], isExampleList = false) {
    this.autocompleteList.innerHTML = '';

    if (isExampleList) {
      const header = document.createElement('div');
      header.className = 'gw-search-example-header';
      header.innerHTML = `<span>💡 Suggested searches & formats:</span>`;
      this.autocompleteList.appendChild(header);
    }

    results.forEach(res => {
      const item = document.createElement('div');
      item.className = 'gw-autocomplete-item';
      item.dataset.lat = String(res.lat);
      item.dataset.lon = String(res.lon);
      item.dataset.name = res.name;

      const title = document.createElement('div');
      title.className = 'gw-ac-title';

      // Feature icon mapping
      let icon = '📍';
      if (res.isCoordinate) icon = '🌐';
      else if (res.kind === 'volcano') icon = '🌋';
      else if (res.kind === 'peak') icon = '🏔️';
      else if (res.kind === 'national_park' || res.kind === 'park') icon = '🌲';
      else if (res.kind === 'state_park') icon = '🏕️';
      else if (res.kind === 'national_monument' || res.kind === 'monument') icon = '🗿';
      else if (res.kind === 'wilderness') icon = '🌲';
      else if (res.kind === 'trail') icon = '🥾';
      else if (res.kind === 'trailhead') icon = '🪧';
      else if (res.kind === 'river') icon = '🏞️';
      else if (res.kind === 'lake' || res.kind === 'waterfall') icon = '💧';
      else if (res.kind === 'city' || res.kind === 'town') icon = '🏙️';
      else if (res.kind === 'county') icon = '🗺️';

      title.innerHTML = `<span class="gw-ac-icon">${icon}</span> <span class="gw-ac-text">${res.name}</span>`;

      const sub = document.createElement('div');
      sub.className = 'gw-ac-sub';
      if (res.subtitle) {
        sub.innerText = res.subtitle;
      } else {
        const metaParts = [];
        if (res.state) metaParts.push(res.state);
        if (res.country && res.country !== 'USA') metaParts.push(res.country);
        if (res.kind) {
          const kindFormatted = res.kind.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
          metaParts.push(kindFormatted);
        }
        sub.innerText = metaParts.join(' • ');
      }

      item.appendChild(title);
      if (sub.innerText) item.appendChild(sub);

      item.addEventListener('click', () => {
        this.handleSelect(res.lat, res.lon, res.name);
        this.inputElement.value = res.name;
        this.closeAutocomplete();
      });

      this.autocompleteList.appendChild(item);
    });

    this.autocompleteList.classList.remove('gw-hidden');
  }

  private handleSelect(lat: number, lon: number, name: string) {
    if (this.onSelectLocation) {
      this.onSelectLocation(lat, lon, name);
    } else {
      this.map.flyTo({ center: [lon, lat], zoom: 11, duration: 1500 });
    }
  }

  private closeAutocomplete() {
    this.autocompleteList.classList.add('gw-hidden');
    this.autocompleteList.innerHTML = '';
  }
}

